import { PictureStorageState, PictureItem } from '../reducers/pictureStorage';
import PictureStorageAction from '../actions/PictureStorageAction';

interface PictureProcess<Image> {
  isReading: boolean;
  isWriting: boolean;
  isEncoding: boolean;
  isDecoding: boolean;
  isSavedOnDisk: boolean;
  blob: Blob | null;
  image: Image | null;
}

function initPictureProcess<Image>(): PictureProcess<Image> {
  return {
    isReading: false,
    isWriting: false,
    isEncoding: false,
    isDecoding: false,
    isSavedOnDisk: false,
    blob: null,
    image: null,
  };
}

// For performance, we hold offline pictures with image instead of blob.

function isReadingDemanded<Image>(
  item: PictureItem<Image>,
  process: PictureProcess<Image>,
): boolean {
  return (
    item.isSavedOnDisk
    && (item.blob === null && process.blob === null)
    && (item.image === null && process.image === null)
    && (item.isImageDemanded || !item.isToBeSaved)
  );
}

function isWritingDemanded<Image>(
  item: PictureItem<Image>,
  process: PictureProcess<Image>,
): boolean {
  return (
    (item.blob !== null || process.blob !== null)
    && item.isToBeSaved
    && !item.isSavedOnDisk
    && !process.isSavedOnDisk
  );
}

function isEncodingDemanded<Image>(
  item: PictureItem<Image>,
  process: PictureProcess<Image>,
): boolean {
  return (
    (item.image !== null || process.image !== null)
    && (item.blob === null && process.blob === null)
    && !item.isSavedOnDisk
    && item.isToBeSaved
  );
}

function isDecodingDemanded<Image>(
  item: PictureItem<Image>,
  process: PictureProcess<Image>,
): boolean {
  return (
    (item.image === null && process.image === null)
    && (item.blob !== null || process.blob !== null)
    && (item.isImageDemanded || !item.isToBeSaved)
  );
}

class PictureStorageWorker<Image> {
  private processes: { [id: string]: PictureProcess<Image> } = {};
  private taskCount = 0;
  private taskResults: PictureStorageAction.TaskResult<Image>[] = [];

  private dispatch: (results: ReadonlyArray<PictureStorageAction.TaskResult<Image>>) => void;
  private read: (id: string) => Promise<Blob>;
  private write: (id: string, blob: Blob) => Promise<void>;
  private encode: (image: Image) => Promise<Blob>;
  private decode: (blob: Blob) => Promise<Image>;

  public constructor(options: {
    dispatch: (results: ReadonlyArray<PictureStorageAction.TaskResult<Image>>) => void,
    read: (id: string) => Promise<Blob>,
    write: (id: string, blob: Blob) => Promise<void>,
    encode: (image: Image) => Promise<Blob>,
    decode: (blob: Blob) => Promise<Image>,
  }) {
    this.dispatch = options.dispatch;
    this.read = options.read;
    this.write = options.write;
    this.encode = options.encode;
    this.decode = options.decode;
  }

  private incrementTaskCount() {
    this.taskCount += 1;
  }

  private decrementTaskCount() {
    this.taskCount -= 1;

    if (this.taskCount === 0) {
      this.processes = {};

      const taskResults = this.taskResults;
      this.taskResults = [];
      this.dispatch(taskResults);
    }
  }

  private advanceProcess(
    id: string,
    item: PictureItem<Image>,
    process: PictureProcess<Image>,
  ) {
    if (!process.isReading && isReadingDemanded(item, process)) {
      process.isReading = true;
      this.incrementTaskCount();

      this.read(id).then((blob) => {
        process.blob = blob;
        process.isReading = false;
        this.taskResults.push({ id, blob, type: PictureStorageAction.TaskType.Reading });
        this.advanceProcess(id, item, process);
        this.decrementTaskCount();
      }).catch((_e) => {
        alert('エラーが発生しました。');
      });
    }

    if (!process.isWriting && isWritingDemanded(item, process)) {
      process.isWriting = true;
      this.incrementTaskCount();

      const blob = process.blob !== null ? process.blob : item.blob;
      if (blob === null) {
        throw new Error('Illegal state.');
      }

      this.write(id, blob).then(() => {
        process.isSavedOnDisk = true;
        process.isWriting = false;
        this.taskResults.push({ id, type: PictureStorageAction.TaskType.Writing });
        this.advanceProcess(id, item, process);
        this.decrementTaskCount();
      }).catch((_e) => {
        alert('エラーが発生しました。');
      });
    }

    if (!process.isEncoding && isEncodingDemanded(item, process)) {
      process.isEncoding = true;
      this.incrementTaskCount();

      const image = process.image !== null ? process.image : item.image;
      if (image === null) {
        throw new Error('Illegal state.');
      }

      this.encode(image).then((blob) => {
        process.blob = blob;
        process.isEncoding = false;
        this.taskResults.push({ id, blob, type: PictureStorageAction.TaskType.Encoding });
        this.advanceProcess(id, item, process);
        this.decrementTaskCount();
      }).catch((_e) => {
        alert('エラーが発生しました。');
      });
    }

    if (!process.isDecoding && isDecodingDemanded(item, process)) {
      process.isDecoding = true;
      this.incrementTaskCount();

      const blob = process.blob !== null ? process.blob : item.blob;
      if (blob === null) {
        throw new Error('Illegal state.');
      }

      this.decode(blob).then((image) => {
        process.image = image;
        process.isDecoding = false;
        this.taskResults.push({ id, image, type: PictureStorageAction.TaskType.Decoding });
        this.advanceProcess(id, item, process);
        this.decrementTaskCount();
      }).catch((_e) => {
        alert('エラーが発生しました。');
      });
    }
  }

  public update(
    prevState: PictureStorageState<Image>,
    state: PictureStorageState<Image>,
  ) {
    if (prevState === state || prevState.items === state.items) {
      return;
    }

    for (const id in state.items) {
      const item = state.items[id];
      if ((id in prevState.items) && prevState.items[id] === item) {
        continue;
      }

      if (!(id in this.processes)) {
        this.processes[id] = initPictureProcess();
      }

      const process = this.processes[id];
      this.advanceProcess(id, item, process);
    }
  }
}

export default PictureStorageWorker;
